class QuantityInput extends HTMLElement {
  connectedCallback() { this.input = this.querySelector('input'); this.addEventListener('click', this.onClick); }
  disconnectedCallback() { this.removeEventListener('click', this.onClick); }
  onClick = (event) => { const button = event.target.closest('button'); if (!button || !this.input) return; event.preventDefault(); button.name === 'plus' ? this.input.stepUp() : this.input.stepDown(); this.input.dispatchEvent(new Event('change', { bubbles: true })); };
}
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

class ProductMedia extends HTMLElement {
  connectedCallback() {
    this.stage = this.querySelector('[data-media-stage]');
    this.addEventListener('click', this.onClick);
    this.stage?.addEventListener('scroll', this.onScroll, { passive: true });
    this.setupModels();

    // A direct variant URL can load with media other than the first slide active.
    // Variant-driven replacements are positioned separately so their move animates.
    if (!this.closest('product-info')?.hasAttribute('data-variant-update') && this.dataset.activeMedia) {
      requestAnimationFrame(() => this.showMedia(this.dataset.activeMedia, 'auto'));
    }
  }

  disconnectedCallback() {
    clearTimeout(this.scrollTimer);
    this.removeEventListener('click', this.onClick);
    this.stage?.removeEventListener('scroll', this.onScroll);
  }

  get items() {
    return this.stage ? [...this.stage.children].filter((item) => item.matches('[data-media-id]')) : [];
  }

  get reduceMotion() {
    return document.documentElement.dataset.motion === 'none'
      || matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  onClick = (event) => {
    const button = event.target.closest('[data-media-target]');
    if (!button) return;
    this.showMedia(button.dataset.mediaTarget);
  };

  onScroll = () => {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => {
      if (!this.stage || !this.items.length) return;
      const stageBounds = this.stage.getBoundingClientRect();
      const center = stageBounds.left + stageBounds.width / 2;
      const active = this.items.reduce((best, item) => {
        const bounds = item.getBoundingClientRect();
        const bestBounds = best.getBoundingClientRect();
        return Math.abs(bounds.left + bounds.width / 2 - center)
          < Math.abs(bestBounds.left + bestBounds.width / 2 - center) ? item : best;
      }, this.items[0]);
      this.setActive(active);
    }, 90);
  };

  findMedia(id) {
    return this.items.find((item) => item.dataset.mediaId === String(id));
  }

  showMedia(id, behavior = this.reduceMotion ? 'auto' : 'smooth') {
    const target = this.findMedia(id);
    if (!target || !this.stage) return false;
    this.setActive(target);
    const stageBounds = this.stage.getBoundingClientRect();
    const targetBounds = target.getBoundingClientRect();
    const left = this.stage.scrollLeft + targetBounds.left - stageBounds.left
      - (this.stage.clientWidth - targetBounds.width) / 2;
    this.stage.scrollTo({ left: Math.max(0, left), behavior });
    return true;
  }

  setActive(active) {
    this.items.forEach((item) => item.classList.toggle('is-active', item === active));
    this.querySelectorAll('[data-media-target]').forEach((item) => {
      item.toggleAttribute('aria-current', item.dataset.mediaTarget === active.dataset.mediaId);
    });
    const status = this.querySelector('[data-media-current]');
    if (status) status.textContent = active.dataset.mediaIndex;
    this.pauseMedia(active);
  }

  pauseMedia(active) {
    this.items.forEach((item) => {
      if (item === active) return;
      item.querySelector('video')?.pause();
      item.querySelector('model-viewer')?.pause?.();
    });
  }

  setupModels() {
    if (!this.querySelector('model-viewer') || !window.Shopify?.loadFeatures) return;
    Shopify.loadFeatures([{
      name: 'model-viewer-ui',
      version: '1.0',
      onLoad: () => this.querySelectorAll('model-viewer').forEach((model) => {
        if (!model.dataset.uiReady) {
          new Shopify.ModelViewerUI(model);
          model.dataset.uiReady = 'true';
        }
      })
    }]);
  }
}
if (!customElements.get('product-media')) customElements.define('product-media', ProductMedia);

class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onOptionChange);
    this.addEventListener('click', this.onStickyClick);
    const addButton = this.querySelector('.product-purchase__add');
    if (addButton) {
      this.purchaseObserver = new IntersectionObserver(
        ([entry]) => this.classList.toggle('product-page--purchase-visible', entry.isIntersecting),
        { threshold: 0.35 }
      );
      this.purchaseObserver.observe(addButton);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.purchaseObserver?.disconnect();
    this.removeEventListener('change', this.onOptionChange);
    this.removeEventListener('click', this.onStickyClick);
  }

  onOptionChange = async (event) => {
    const input = event.target.closest('[data-option-value-id]');
    if (!input) return;
    const values = [...this.querySelectorAll('[data-option-value-id]:checked')]
      .map((item) => item.dataset.optionValueId);
    if (!values.length) return;

    this.controller?.abort();
    this.controller = new AbortController();
    this.setAttribute('aria-busy', 'true');
    const base = input.dataset.productUrl || this.dataset.productUrl;
    const url = new URL(base, location.origin);
    url.searchParams.set('option_values', values.join(','));
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error(response.status);
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = parsed.getElementById(this.id);
      if (!next) throw new Error('Product section unavailable');

      const currentMediaWrap = this.querySelector('.product-page__media');
      const currentGallery = currentMediaWrap?.querySelector('product-media');
      const nextMediaWrap = next.querySelector('.product-page__media');
      const nextGallery = nextMediaWrap?.querySelector('product-media');
      const targetMediaId = nextGallery?.dataset.activeMedia;

      // Preserve the live carousel whenever this product already contains the
      // variant image. Its existing scroll position makes the change a real slide
      // instead of resetting the gallery before moving to the new image.
      if (targetMediaId && currentGallery?.findMedia(targetMediaId) && nextMediaWrap) {
        currentGallery.dataset.activeMedia = targetMediaId;
        nextMediaWrap.replaceWith(currentMediaWrap);
      }

      next.setAttribute('data-variant-update', '');
      const selectedUrl = next.dataset.selectedUrl || base;
      this.replaceWith(next);
      history.replaceState({}, '', selectedUrl);

      requestAnimationFrame(() => {
        const gallery = next.querySelector('product-media');
        gallery?.showMedia(targetMediaId);
        next.removeAttribute('data-variant-update');
        next.querySelector(`[data-option-value-id="${input.dataset.optionValueId}"]`)?.focus();
      });
    } catch (error) {
      if (error.name !== 'AbortError') this.showError(this.dataset.variantError);
    } finally {
      this.removeAttribute('aria-busy');
    }
  };

  onStickyClick = (event) => {
    const button = event.target.closest('[data-sticky-submit]');
    if (button) this.querySelector('.product-purchase__form button[name="add"]')?.click();
  };

  showError(message) {
    const region = this.querySelector('[data-product-error]');
    if (region) {
      region.textContent = message;
      region.hidden = false;
    }
  }
}
if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);